import { Injectable } from '@angular/core';
import { LegView } from '@sequence/models/view/leg.view';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { DecsService } from '@shared/services/decs/decs.service';
import { Observable, map, of } from 'rxjs';
import { ArmedPax, ArmedPaxView } from './models/armed-pax.view';
import { DateTime } from 'luxon';
import { AlertService } from '@shared/services/alert/alert.service';

@Injectable({
  providedIn: 'root',
})
export class ArmedPaxStore {
  constructor(
    private decsService: DecsService,
    private alertService: AlertService,
  ) {}

  getArmedPax(
    flight: LegView,
    isRefresh = false,
  ): Observable<RequestState<ArmedPaxView>> {
    // Only check within 1 day of departure.
    if (!this.isAvailable(flight)) {
      return of(
        new RequestState<ArmedPaxView>(RequestStatus.IsCompleteWithResponse, {
          armedPax: [],
          timestamp: DateTime.now(),
        }),
      );
    }

    const cmd = `G*L${flight.flightNumber}/${
      flight.depTimeSched?.toFormat('ddMMM') || ''
    }${flight.depStation}/ARMD`;

    return this.decsService.submitResRequest({ cmd }, isRefresh).pipe(
      map((response) => {
        if (response.hasError) {
          this.showErrorAlert();
        }

        if (!response.response) {
          return response.into<ArmedPaxView>();
        }

        const sabreText = response.response.result;

        return response.into<ArmedPaxView>(
          this.parseSabreText(sabreText, response.response.timestamp),
        );
      }),
    );
  }

  // Check if scheduled departure is within 1 calendar days.
  private isAvailable(leg: LegView): boolean {
    if (!leg.depTimeSchedUTC) {
      return false;
    }

    const diff = leg.depTimeSchedUTC.diff(DateTime.now().toUTC()).as('days');

    if (diff < 1) {
      return true;
    }

    return false;
  }

  private parseSabreText(text: string, timestamp: string): ArmedPaxView {
    const armedPax: ArmedPax[] = [];

    const namePattern = /\d{1,2}\s{2}[A-Z]+/;

    const lines = text.split('\n');

    for (const line of lines) {
      if (namePattern.test(line) && line.length > 40) {
        const firstName = line.substring(17, 22);
        const lastName = line.substring(4, 17);
        const seat = line.substring(36, 39);

        armedPax.push({
          name: `${firstName} ${lastName}`,
          seat,
        });
      }
    }

    return {
      armedPax,
      sabreText: text,
      timestamp: DateTime.fromISO(timestamp),
    };
  }

  private showErrorAlert(): void {
    this.alertService.setAlert({
      type: 'error',
      message: 'Unable to get armed passenger list.',
    });
  }
}
